import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import { QA_ALLOWLIST, UPDATE_QA_ALLOWLIST } from './graphql';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const QaAllowlistPanel = () => {
  const { data, loading, refetch } = useQuery(QA_ALLOWLIST, { fetchPolicy: 'network-only' });
  const [save, { loading: saving, error: saveError }] = useMutation(UPDATE_QA_ALLOWLIST, {
    onCompleted: () => refetch(),
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const allowlist: string[] = data?.qaAllowlist ?? [];

  useEffect(() => {
    if (!editing) setDraft(allowlist.join('\n'));
  }, [allowlist, editing]);

  const parsedDraft = draft
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const invalidLines = parsedDraft.filter((id) => !UUID_RE.test(id));
  const canSave = invalidLines.length === 0 && !saving;

  const handleSave = () => {
    save({ variables: { input: { userIds: parsedDraft } } }).then(() => setEditing(false));
  };

  const handleCancel = () => {
    setDraft(allowlist.join('\n'));
    setEditing(false);
  };

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex align-items-center">
        <span className="font-weight-bold">
          <i className="fas fa-user-shield mr-2" />
          UserID Allowlist
        </span>
        {!editing && (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => setEditing(true)}
            disabled={loading}
            className="ml-3">
            <i className="fas fa-pencil-alt mr-1" />
            Edit
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        {loading && (
          <div className="text-muted">
            <Spinner as="span" animation="border" size="sm" className="mr-2" />
            Loading...
          </div>
        )}

        {!loading && !editing && (
          <>
            {allowlist.length === 0 ? (
              <Alert variant="warning" className="py-2 mb-0">
                <i className="fas fa-exclamation-triangle mr-2" />
                Empty — import is disabled. Click <b>Edit</b> to add userIDs.
              </Alert>
            ) : (
              <div>
                {allowlist.map((id) => (
                  <Badge key={id} variant="secondary" className="mr-2 mb-2" style={{ fontSize: 13 }}>
                    <code className="text-light">{id}</code>
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && editing && (
          <Form>
            <Form.Group>
              <Form.Label>QA user UUIDs (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
                placeholder={'e.g. 11111111-2222-3333-4444-555555555555\n(one UUID per line)'}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
                className="dimmed-placeholder"
                isInvalid={invalidLines.length > 0}
              />
              {invalidLines.length > 0 && (
                <Form.Text className="text-danger">
                  Invalid UUID(s): {invalidLines.join(', ')}
                </Form.Text>
              )}
            </Form.Group>

            <div className="d-flex" style={{ gap: 8 }}>
              <Button variant="primary" disabled={!canSave} onClick={handleSave}>
                {saving ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Allowlist'
                )}
              </Button>
              <Button variant="secondary" disabled={saving} onClick={handleCancel}>
                Cancel
              </Button>
            </div>

            {saveError && (
              <Alert variant="danger" className="mt-3 py-2">
                {saveError.message}
              </Alert>
            )}
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

export default QaAllowlistPanel;
